import { useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Code,
  CodeBlock,
  Dialog,
  Field,
  Input,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from "@one-nexty/components-web";
import {
  OneNextyLogo,
  OneNextyMark,
  AnimatedOneNextyLogo,
  NextyBrandLogo,
  NextyBrandMark,
  NextyWordmark,
  NextyAppIcon,
  NextyFavicon,
  AnimatedNextyLogo,
  NextyBrandSpinner,
  NextyDesignSystemLogo,
  NextyDesignSystemMark,
  NextyArthurLogo,
  NextyArthurMark,
  NextyCloudLogo,
  NextyCloudMark,
} from "@one-nexty/icons";

import { Section, SubSection } from "../ui/section";

export interface LogoDownloadConfig {
  brand: "one-nexty" | "nexty" | "design-system" | "arthur" | "cloud";
  variant: "horizontal" | "stacked" | "mark" | "wordmark" | "app-icon" | "favicon";
  color: "primary" | "monochrome" | "white";
  format: "svg" | "png" | "react";
  size: number;
  background: "transparent" | "dark" | "light";
}

const DEFAULT_CONFIG: LogoDownloadConfig = {
  brand: "one-nexty",
  variant: "horizontal",
  color: "primary",
  format: "svg",
  size: 256,
  background: "dark",
};

export function BrandSection() {
  const [previewTheme, setPreviewTheme] = useState<"light" | "dark">("dark");
  const [autoAnimate, setAutoAnimate] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  // Overlay Exporter State
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [downloadConfig, setDownloadConfig] = useState<LogoDownloadConfig>(DEFAULT_CONFIG);

  const openExporter = (override?: Partial<LogoDownloadConfig>) => {
    setDownloadConfig((prev) => ({
      ...prev,
      ...override,
    }));
    setIsDialogOpen(true);
  };

  const copyCode = (code: string, label: string) => {
    navigator.clipboard.writeText(code);
    setCopiedSnippet(label);
    setTimeout(() => setCopiedSnippet(null), 2000);
  };

  const activeColor = previewTheme === "dark" ? "white" : "primary";

  return (
    <Section
      id="brand-logos"
      eyebrow="Fondations"
      title="Marque & logos"
      description="Logos vectoriels de l'entité, de la marque produit et de l'écosystème, en SVG statique ou animé."
    >
      {/* ------------------------------------------------ Barre d'actions globale */}
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-border bg-surface-raised p-4">
        <div className="flex items-center gap-3">
          <span className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
            Fond d'aperçu :
          </span>
          <div className="inline-flex rounded-lg border border-border bg-surface p-1">
            <button
              onClick={() => setPreviewTheme("dark")}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                previewTheme === "dark"
                  ? "bg-slate-900 text-white shadow-sm"
                  : "text-fg-muted hover:text-fg"
              }`}
            >
              Sombre (Dark)
            </button>
            <button
              onClick={() => setPreviewTheme("light")}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                previewTheme === "light"
                  ? "bg-white text-slate-950 shadow-sm"
                  : "text-fg-muted hover:text-fg"
              }`}
            >
              Clair (Light)
            </button>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-xs font-medium text-fg-muted cursor-pointer select-none">
            <input
              type="checkbox"
              checked={autoAnimate}
              onChange={(e) => setAutoAnimate(e.target.checked)}
              className="rounded border-border text-accent focus:ring-accent"
            />
            Animation en boucle
          </label>

          <Button
            size="sm"
            variant="primary"
            onClick={() => openExporter({ brand: "one-nexty", variant: "horizontal" })}
          >
            Télécharger & Exporter un logo
          </Button>
        </div>
      </div>

      <Tabs defaultValue="corporate" className="w-full">
        <TabList aria-label="Sections des marques">
          <Tab value="corporate">One Nexty (Entreprise)</Tab>
          <Tab value="product">nexty (Brand Parent)</Tab>
          <Tab value="ecosystem">Produits Écosystème (3 Logos)</Tab>
        </TabList>

        {/* ==================================================================== */}
        {/* TAB 1: ONE NEXTY (CORPORATE / ENTERPRISE)                            */}
        {/* ==================================================================== */}
        <TabPanel value="corporate" className="space-y-10 pt-6">
          {/* Hero Présentation Corporate */}
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand/10 via-surface-raised to-accent/5 p-8">
            <div className="relative z-10 max-w-3xl">
              <Badge tone="brand" className="mb-4">
                Holding & Entreprise
              </Badge>
              <h3 className="text-2xl font-bold tracking-tight text-fg sm:text-3xl">
                Identité Institutionnelle One Nexty
              </h3>
              <p className="mt-2 text-sm text-fg-muted leading-relaxed">
                Le logo corporate <strong className="text-fg">One Nexty</strong> incarne la structure parent,
                l'excellence d'ingénierie et la vision stratégique globale. Il associe le monogramme géométrique
                <Code className="mx-1">1N</Code> (Squircle d'unité) avec une typographie de prestige.
              </p>
            </div>
            <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
              <OneNextyMark size={240} color="gradient" />
            </div>
          </div>

          {/* Grille des Déclinaisons Corporate */}
          <SubSection title="Déclinaisons Officielle One Nexty">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Card 1: Logo Principal Horizontal */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Logo Full Horizontal</h4>
                      <p className="text-xs text-fg-muted">Format en-tête, navbar corporate, rapports</p>
                    </div>
                    <Badge tone="neutral">Standard</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                    }`}
                  >
                    <OneNextyLogo
                      variant="horizontal"
                      color={activeColor}
                      size={42}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "one-nexty", variant: "horizontal" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(
                          `<OneNextyLogo variant="horizontal" size={42} />`,
                          "corporate-horizontal"
                        )
                      }
                    >
                      {copiedSnippet === "corporate-horizontal" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 2: Logo Stacked (Centré) */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Logo Centré (Stacked)</h4>
                      <p className="text-xs text-fg-muted">Présentations, splash, couvertures de documents</p>
                    </div>
                    <Badge tone="neutral">Centré</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                    }`}
                  >
                    <OneNextyLogo
                      variant="stacked"
                      color={activeColor}
                      size={38}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "one-nexty", variant: "stacked" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(
                          `<OneNextyLogo variant="stacked" size={38} />`,
                          "corporate-stacked"
                        )
                      }
                    >
                      {copiedSnippet === "corporate-stacked" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 3: Monogramme Corporate (Mark Only) */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Monogramme 1N (Mark Only)</h4>
                      <p className="text-xs text-fg-muted">Icônes compactes, filigranes, favicon corporate</p>
                    </div>
                    <Badge tone="neutral">Symbol</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center gap-6 rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950" : "bg-slate-100"
                    }`}
                  >
                    <OneNextyMark color="primary" size={32} />
                    <OneNextyMark color="primary" size={44} />
                    <OneNextyMark color="primary" size={56} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "one-nexty", variant: "mark" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<OneNextyMark color="primary" size={44} />`, "corporate-mark")
                      }
                    >
                      {copiedSnippet === "corporate-mark" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 4: Logo Animé Interactif */}
              <Card className="overflow-hidden border-indigo-500/40 bg-indigo-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Logo Corporate Animé</h4>
                      <p className="text-xs text-fg-muted">Effet survol, tracé dynamique & lueur</p>
                    </div>
                    <Badge tone="brand">Animé</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                    }`}
                  >
                    <AnimatedOneNextyLogo
                      variant="full"
                      color={activeColor}
                      size={44}
                      autoAnimate={autoAnimate}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "one-nexty", variant: "horizontal", format: "react" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(
                          `<AnimatedOneNextyLogo autoAnimate={true} size={44} />`,
                          "corporate-animated"
                        )
                      }
                    >
                      {copiedSnippet === "corporate-animated" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </SubSection>
        </TabPanel>

        {/* ==================================================================== */}
        {/* TAB 2: NEXTY (PRODUCT BRAND / PARENT)                                */}
        {/* ==================================================================== */}
        <TabPanel value="product" className="space-y-10 pt-6">
          {/* Hero Présentation Produit */}
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand/10 via-surface-raised to-accent/5 p-8">
            <div className="relative z-10 max-w-3xl">
              <Badge tone="brand" className="mb-4">
                Product & Tech Brand
              </Badge>
              <h3 className="text-2xl font-bold tracking-tight text-fg sm:text-3xl">
                Marque Produit nexty.
              </h3>
              <p className="mt-2 text-sm text-fg-muted leading-relaxed">
                La marque produit <strong className="text-fg">nexty</strong> incarne la vélocité, l'innovation UI/UX
                et la plateforme technique. Son emblème <Code className="mx-1">n</Code> aux courbes fluides est complété
                par le point d'accentuation signature bleu royal <Code className="mx-1">nexty.</Code>.
              </p>
            </div>
            <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
              <NextyBrandMark size={240} color="gradient" />
            </div>
          </div>

          {/* Grille des Déclinaisons Produit */}
          <SubSection title="Déclinaisons Marque Produit nexty">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Card 1: Logo Produit Full */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Logo Produit complet</h4>
                      <p className="text-xs text-fg-muted">Emblème 'n' + Wordmark 'nexty.'</p>
                    </div>
                    <Badge tone="neutral">Product Standard</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                    }`}
                  >
                    <NextyBrandLogo
                      color={activeColor}
                      size={36}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "nexty", variant: "horizontal" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyBrandLogo size={36} />`, "product-full")
                      }
                    >
                      {copiedSnippet === "product-full" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 2: App Icon Squircle (macOS / iOS) */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">App Icon Squircle</h4>
                      <p className="text-xs text-fg-muted">Format d'icône d'application iOS / macOS</p>
                    </div>
                    <Badge tone="neutral">Squircle</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center gap-6 rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950" : "bg-slate-100"
                    }`}
                  >
                    <NextyAppIcon size={48} />
                    <NextyAppIcon size={64} />
                    <NextyAppIcon size={80} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "nexty", variant: "app-icon" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyAppIcon size={64} />`, "product-appicon")
                      }
                    >
                      {copiedSnippet === "product-appicon" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 3: Favicon & Petits Formats */}
              <Card className="overflow-hidden">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Favicon Navigateur & Avatars</h4>
                      <p className="text-xs text-fg-muted">Tailles optimisées 16px, 24px, 32px, 48px</p>
                    </div>
                    <Badge tone="neutral">Compact</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center gap-6 rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950" : "bg-slate-100"
                    }`}
                  >
                    <NextyFavicon size={24} />
                    <NextyFavicon size={32} />
                    <NextyFavicon size={48} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "nexty", variant: "favicon" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyFavicon size={32} />`, "product-favicon")
                      }
                    >
                      {copiedSnippet === "product-favicon" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Card 4: Logo Animé Produit & Brand Spinner */}
              <Card className="overflow-hidden border-blue-500/40 bg-blue-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Logo Animé & Loader de Marque</h4>
                      <p className="text-xs text-fg-muted">Animation interactive + Spinner de chargement</p>
                    </div>
                    <Badge tone="brand">Motion & Loaders</Badge>
                  </div>

                  <div
                    className={`flex h-32 items-center justify-center gap-10 rounded-xl border border-border/50 p-6 transition-colors ${
                      previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                    }`}
                  >
                    <AnimatedNextyLogo size={36} color={activeColor} autoAnimate={autoAnimate} />
                    <div className="h-8 w-px bg-border" />
                    <NextyBrandSpinner size={36} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "nexty", variant: "horizontal", format: "react" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(
                          `<AnimatedNextyLogo size={36} autoAnimate={true} />`,
                          "product-animated"
                        )
                      }
                    >
                      {copiedSnippet === "product-animated" ? "Copié !" : "Copier Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </SubSection>
        </TabPanel>

        {/* ==================================================================== */}
        {/* TAB 3: PRODUITS DE L'ÉCOSYSTÈME (3 LOGOS APPRÈS NEXTY.)                */}
        {/* ==================================================================== */}
        <TabPanel value="ecosystem" className="space-y-10 pt-6">
          {/* Hero Présentation Écosystème */}
          <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand/10 via-surface-raised to-accent/5 p-8">
            <div className="relative z-10 max-w-3xl">
              <Badge tone="brand" className="mb-4">
                Écosystème Produit (3 Produits)
              </Badge>
              <h3 className="text-2xl font-bold tracking-tight text-fg sm:text-3xl">
                Logos Produits Écosystème nexty.
              </h3>
              <p className="mt-2 text-sm text-fg-muted leading-relaxed">
                Les trois piliers applicatifs de l'écosystème s'affichent avec leur marque parent :{" "}
                <Code className="mx-1 font-semibold text-blue-500">nexty. design</Code>,{" "}
                <Code className="mx-1 font-semibold text-rose-500">nexty. arthur</Code> et{" "}
                <Code className="mx-1 font-semibold text-sky-500">nexty. cloud</Code>.
              </p>
            </div>
          </div>

          <SubSection title="Les 3 Logos Produits de l'Écosystème">
            <div className="grid gap-6 md:grid-cols-3">
              {/* Produit 1: nexty. design-system */}
              <Card className="overflow-hidden border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">nexty. design system</h4>
                      <p className="text-xs text-fg-muted">Tokens, Composants & UI</p>
                    </div>
                    <Badge tone="success">Produit 01</Badge>
                  </div>

                  <div
                    className={`flex h-36 flex-col items-center justify-center gap-3 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyDesignSystemLogo color={activeColor} size={32} />
                    <div className="flex items-center gap-3 pt-2 border-t border-border/40 w-full justify-center">
                      <NextyDesignSystemMark color="primary" size={24} />
                      <span className="text-xs font-mono text-fg-muted">Mark & Palette</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "design-system", variant: "horizontal" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyDesignSystemLogo size={32} />`, "ds-logo")
                      }
                    >
                      {copiedSnippet === "ds-logo" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Produit 2: nexty. arthur */}
              <Card className="overflow-hidden border-rose-200 dark:border-rose-500/30 bg-rose-50/40 dark:bg-rose-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">nexty. arthur</h4>
                      <p className="text-xs text-fg-muted">Moteur IA & Expérience</p>
                    </div>
                    <Badge tone="danger">Produit 02</Badge>
                  </div>

                  <div
                    className={`flex h-36 flex-col items-center justify-center gap-3 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyArthurLogo color={activeColor} size={32} />
                    <div className="flex items-center gap-3 pt-2 border-t border-border/40 w-full justify-center">
                      <NextyArthurMark color="primary" size={24} />
                      <span className="text-xs font-mono text-fg-muted">Spark Core AI</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "arthur", variant: "horizontal" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyArthurLogo size={32} />`, "arthur-logo")
                      }
                    >
                      {copiedSnippet === "arthur-logo" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Produit 3: nexty. cloud */}
              <Card className="overflow-hidden border-sky-200 dark:border-sky-500/30 bg-sky-50/40 dark:bg-sky-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">nexty. cloud</h4>
                      <p className="text-xs text-fg-muted">Infrastructure & GCP Mesh</p>
                    </div>
                    <Badge tone="info">Produit 03</Badge>
                  </div>

                  <div
                    className={`flex h-36 flex-col items-center justify-center gap-3 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyCloudLogo color={activeColor} size={32} />
                    <div className="flex items-center gap-3 pt-2 border-t border-border/40 w-full justify-center">
                      <NextyCloudMark color="primary" size={24} />
                      <span className="text-xs font-mono text-fg-muted">Cloud Mesh</span>
                    </div>
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "cloud", variant: "horizontal" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyCloudLogo size={32} />`, "cloud-logo")
                      }
                    >
                      {copiedSnippet === "cloud-logo" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </SubSection>

          {/* SubSection 2: Mini-Logos / Emblèmes Indépendants */}
          <SubSection title="Mini-Logos & Emblèmes Produits Indépendants (Mark Only)">
            <div className="grid gap-6 md:grid-cols-3">
              {/* Mini Logo 1: Design System Mark */}
              <Card className="overflow-hidden border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/30 dark:bg-emerald-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Mini-Logo design system</h4>
                      <p className="text-xs text-fg-muted">Icône autonome, favicon, avatar UI</p>
                    </div>
                    <Badge tone="success">Mark Only</Badge>
                  </div>

                  <div
                    className={`flex h-36 items-center justify-center gap-6 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyDesignSystemMark color="primary" size={24} />
                    <NextyDesignSystemMark color="primary" size={36} />
                    <NextyDesignSystemMark color="primary" size={48} />
                    <NextyDesignSystemMark color="primary" size={60} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "design-system", variant: "mark" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyDesignSystemMark size={48} />`, "ds-mark")
                      }
                    >
                      {copiedSnippet === "ds-mark" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Mini Logo 2: Arthur Mark */}
              <Card className="overflow-hidden border-rose-200 dark:border-rose-500/30 bg-rose-50/30 dark:bg-rose-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Mini-Logo arthur</h4>
                      <p className="text-xs text-fg-muted">Bouton IA, trigger assistant floating</p>
                    </div>
                    <Badge tone="danger">Mark Only</Badge>
                  </div>

                  <div
                    className={`flex h-36 items-center justify-center gap-6 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyArthurMark color="primary" size={24} />
                    <NextyArthurMark color="primary" size={36} />
                    <NextyArthurMark color="primary" size={48} />
                    <NextyArthurMark color="primary" size={60} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "arthur", variant: "mark" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyArthurMark size={48} />`, "arthur-mark")
                      }
                    >
                      {copiedSnippet === "arthur-mark" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>

              {/* Mini Logo 3: Cloud Mark */}
              <Card className="overflow-hidden border-sky-200 dark:border-sky-500/30 bg-sky-50/30 dark:bg-sky-950/10">
                <CardBody className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-fg">Mini-Logo cloud</h4>
                      <p className="text-xs text-fg-muted">Indicateur de statut, badge cloud</p>
                    </div>
                    <Badge tone="info">Mark Only</Badge>
                  </div>

                  <div
                    className={`flex h-36 items-center justify-center gap-6 rounded-xl border p-6 transition-colors ${
                      previewTheme === "dark"
                        ? "bg-slate-950 text-white border-border/50 shadow-inner"
                        : "bg-white text-slate-950 border-slate-200/80 shadow-sm"
                    }`}
                  >
                    <NextyCloudMark color="primary" size={24} />
                    <NextyCloudMark color="primary" size={36} />
                    <NextyCloudMark color="primary" size={48} />
                    <NextyCloudMark color="primary" size={60} />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openExporter({ brand: "cloud", variant: "mark" })}
                    >
                      Exporter / Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() =>
                        copyCode(`<NextyCloudMark size={48} />`, "cloud-mark")
                      }
                    >
                      {copiedSnippet === "cloud-mark" ? "Copié !" : "Code"}
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </div>
          </SubSection>

          {/* Tableau récapitulatif de l'écosystème */}
          <SubSection title="Synthèse des Marqueurs Produits">
            <div className="rounded-xl border border-border bg-surface-raised p-6 space-y-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-fg-muted">
                Déclinaisons Typographiques Écosystème :
              </p>
              <div
                className={`grid gap-4 md:grid-cols-3 p-4 rounded-lg transition-colors ${
                  previewTheme === "dark" ? "bg-slate-950 text-white" : "bg-slate-100 text-slate-950"
                }`}
              >
                <div className="flex flex-col gap-2 items-center justify-center p-3 border border-border/40 rounded-lg">
                  <NextyDesignSystemLogo color={activeColor} size={28} />
                  <span className="text-[10px] text-fg-muted font-mono">@one-nexty/design-system</span>
                </div>
                <div className="flex flex-col gap-2 items-center justify-center p-3 border border-border/40 rounded-lg">
                  <NextyArthurLogo color={activeColor} size={28} />
                  <span className="text-[10px] text-fg-muted font-mono">@one-nexty/arthur</span>
                </div>
                <div className="flex flex-col gap-2 items-center justify-center p-3 border border-border/40 rounded-lg">
                  <NextyCloudLogo color={activeColor} size={28} />
                  <span className="text-[10px] text-fg-muted font-mono">@one-nexty/cloud</span>
                </div>
              </div>
            </div>
          </SubSection>
        </TabPanel>
      </Tabs>

      {/* ------------------------------------------------ Integration & Snippet Code */}
      <SubSection title="Utiliser les Logos des Produits dans votre Projet">
        <CodeBlock title="Exemple d'intégration dans une Application React">{`
import {
  NextyDesignSystemLogo,
  NextyArthurLogo,
  NextyCloudLogo
} from "@one-nexty/icons";

export function AppHeader() {
  return (
    <header className="flex items-center justify-between px-6 py-4">
      {/* Product 1: nexty. design-system */}
      <NextyDesignSystemLogo size={32} />

      {/* Product 2: nexty. arthur */}
      <NextyArthurLogo size={32} />

      {/* Product 3: nexty. cloud */}
      <NextyCloudLogo size={32} />
    </header>
  );
}
`}</CodeBlock>
      </SubSection>

      {/* ------------------------------------------------ Modal Dialog Exporter */}
      <LogoExportDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        config={downloadConfig}
        setConfig={setDownloadConfig}
      />
    </Section>
  );
}

interface LogoExportDialogProps {
  open: boolean;
  onClose: () => void;
  config: LogoDownloadConfig;
  setConfig: React.Dispatch<React.SetStateAction<LogoDownloadConfig>>;
}

function LogoExportDialog({ open, onClose, config, setConfig }: LogoExportDialogProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [downloadSuccess, setDownloadSuccess] = useState<string | null>(null);

  const update = <K extends keyof LogoDownloadConfig>(key: K, value: LogoDownloadConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const handleDownload = () => {
    if (config.format === "react") {
      const reactSnippet = generateReactSnippet(config);
      navigator.clipboard.writeText(reactSnippet);
      setDownloadSuccess("Code React copié dans le presse-papier !");
      setTimeout(() => setDownloadSuccess(null), 2500);
      return;
    }

    const svgContainer = previewRef.current;
    if (!svgContainer) return;

    const svgElement = svgContainer.querySelector("svg");
    if (!svgElement) return;

    const filename = `${config.brand}-${config.variant}-${config.color}-${config.size}px`;
    const isWide = config.variant === "horizontal" || config.variant === "stacked";
    const exportWidth = isWide ? config.size * 3.5 : config.size;
    const exportHeight = config.size;

    if (config.format === "svg") {
      const clone = svgElement.cloneNode(true) as SVGElement;
      clone.setAttribute("width", String(exportWidth));
      clone.setAttribute("height", String(exportHeight));

      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(clone);
      if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
      }
      const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${filename}.svg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setDownloadSuccess(`Fichier ${filename}.svg (${exportWidth}x${exportHeight}px) téléchargé !`);
      setTimeout(() => setDownloadSuccess(null), 2500);
    } else if (config.format === "png") {
      const clone = svgElement.cloneNode(true) as SVGElement;
      clone.setAttribute("width", String(exportWidth));
      clone.setAttribute("height", String(exportHeight));

      const serializer = new XMLSerializer();
      let source = serializer.serializeToString(clone);
      if (!source.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
        source = source.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = exportWidth;
        canvas.height = exportHeight;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          if (config.background === "light") {
            ctx.fillStyle = "#FFFFFF";
            ctx.fillRect(0, 0, exportWidth, exportHeight);
          } else if (config.background === "dark") {
            ctx.fillStyle = "#090D16";
            ctx.fillRect(0, 0, exportWidth, exportHeight);
          }

          ctx.drawImage(img, 0, 0, exportWidth, exportHeight);

          canvas.toBlob((blob) => {
            if (blob) {
              const pngUrl = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = pngUrl;
              a.download = `${filename}.png`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(pngUrl);
              setDownloadSuccess(`Image ${filename}.png (${exportWidth}x${exportHeight}px) téléchargée !`);
              setTimeout(() => setDownloadSuccess(null), 2500);
            }
          }, "image/png");
        }
        URL.revokeObjectURL(url);
      };
      img.src = url;
    }
  };

  const previewDisplaySize =
    config.variant === "horizontal"
      ? 42
      : config.variant === "stacked"
      ? 44
      : config.variant === "mark" || config.variant === "favicon"
      ? 48
      : 56;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Configurateur d'Exportation de Logo"
      description="Format, résolution, couleur et fond, avant téléchargement."
      footer={
        <div className="flex w-full items-center justify-between gap-4">
          <span className="text-xs font-medium text-emerald-400">
            {downloadSuccess || ""}
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              Fermer
            </Button>
            <Button variant="primary" onClick={handleDownload}>
              {config.format === "react"
                ? "Copier le code React"
                : `Télécharger .${config.format.toUpperCase()}`}
            </Button>
          </div>
        </div>
      }
    >
      <div className="grid gap-6 md:grid-cols-12">
        {/* Colonne de Gauche: Aperçu Live Cadré & Proportionnel */}
        <div className="md:col-span-5 flex flex-col items-center justify-center rounded-xl border border-border/80 p-6 relative overflow-hidden min-h-[220px]">
          <div
            className={`absolute inset-0 transition-colors ${
              config.background === "dark"
                ? "bg-slate-950 text-white"
                : config.background === "light"
                ? "bg-white text-slate-950"
                : "bg-surface-raised text-fg"
            }`}
          />
          {config.background === "transparent" && (
            <div className="absolute inset-0 bg-[radial-gradient(#334155_1px,transparent_1px)] [background-size:16px_16px] opacity-20 pointer-events-none" />
          )}

          <div
            ref={previewRef}
            className="relative z-10 flex items-center justify-center p-4 max-h-[160px] w-full max-w-full overflow-hidden [&_svg]:max-h-[110px] [&_svg]:w-auto [&_svg]:max-w-full"
          >
            <RenderLogoConfig config={config} overrideSize={previewDisplaySize} />
          </div>
          <span className="relative z-10 mt-4 text-[11px] font-mono text-fg-muted">
            Exportation : {config.size}px · {config.format.toUpperCase()} · {config.color}
          </span>
        </div>

        {/* Colonne de Droite: Contrôles de Configuration */}
        <div className="md:col-span-7 space-y-4">
          {/* Sélection de la Marque & Variante */}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Entité / Marque Produit">
              <select
                value={config.brand}
                onChange={(e) => update("brand", e.target.value as any)}
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg"
              >
                <option value="one-nexty">One Nexty (Corporate)</option>
                <option value="nexty">nexty (Brand Parent)</option>
                <option value="design-system">nexty. design system</option>
                <option value="arthur">nexty. arthur</option>
                <option value="cloud">nexty. cloud</option>
              </select>
            </Field>

            <Field label="Variante">
              <select
                value={config.variant}
                onChange={(e) => update("variant", e.target.value as any)}
                className="w-full rounded-md border border-border bg-surface px-3 py-1.5 text-sm text-fg"
              >
                <option value="horizontal">Horizontal (Full)</option>
                <option value="stacked">Centré (Stacked)</option>
                <option value="mark">Symbole / Monogramme</option>
                {config.brand === "nexty" && <option value="wordmark">Wordmark (nexty.)</option>}
                {config.brand === "nexty" && <option value="app-icon">App Icon Squircle</option>}
                {config.brand === "nexty" && <option value="favicon">Favicon Compact</option>}
              </select>
            </Field>
          </div>

          {/* Sélection du Format */}
          <Field label="Format d'Exportation">
            <div className="flex gap-2">
              <FormatButton
                active={config.format === "svg"}
                onClick={() => update("format", "svg")}
                label="SVG (Vectoriel)"
              />
              <FormatButton
                active={config.format === "png"}
                onClick={() => update("format", "png")}
                label="PNG (Image HD)"
              />
              <FormatButton
                active={config.format === "react"}
                onClick={() => update("format", "react")}
                label="Code React"
              />
            </div>
          </Field>

          {/* Couleur & Palette */}
          <Field label="Palette & Style de Couleur">
            <div className="flex gap-2">
              <FormatButton
                active={config.color === "primary"}
                onClick={() => update("color", "primary")}
                label="Primary / Dégradé"
              />
              <FormatButton
                active={config.color === "monochrome"}
                onClick={() => update("color", "monochrome")}
                label="Monochrome Sombre"
              />
              <FormatButton
                active={config.color === "white"}
                onClick={() => update("color", "white")}
                label="Blanc (Light)"
              />
            </div>
          </Field>

          {/* Tailles & Presets */}
          <div className="grid grid-cols-12 gap-3 items-end">
            <div className="col-span-7">
              <Field label="Taille d'exportation (px)">
                <div className="flex flex-wrap gap-1.5">
                  {[32, 64, 128, 256, 512, 1024].map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => update("size", s)}
                      className={`rounded px-2.5 py-1 text-xs font-mono transition-colors ${
                        config.size === s
                          ? "bg-accent text-accent-fg font-semibold"
                          : "border border-border bg-surface text-fg-muted hover:text-fg"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </Field>
            </div>
            <div className="col-span-5">
              <Field label="Taille personnalisée">
                <Input
                  type="number"
                  value={config.size}
                  onChange={(e) => update("size", Number(e.target.value) || 64)}
                  min={16}
                  max={2048}
                />
              </Field>
            </div>
          </div>

          {/* Arrière-plan pour export PNG */}
          {config.format === "png" && (
            <Field label="Arrière-plan d'exportation PNG">
              <div className="flex gap-2">
                <FormatButton
                  active={config.background === "transparent"}
                  onClick={() => update("background", "transparent")}
                  label="Transparent"
                />
                <FormatButton
                  active={config.background === "dark"}
                  onClick={() => update("background", "dark")}
                  label="Fond Sombre (#090D16)"
                />
                <FormatButton
                  active={config.background === "light"}
                  onClick={() => update("background", "light")}
                  label="Fond Blanc (#FFFFFF)"
                />
              </div>
            </Field>
          )}
        </div>
      </div>
    </Dialog>
  );
}

function FormatButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors border ${
        active
          ? "border-accent bg-accent-subtle text-accent-fg font-semibold"
          : "border-border bg-surface text-fg-muted hover:text-fg hover:bg-surface-raised"
      }`}
    >
      {label}
    </button>
  );
}

function RenderLogoConfig({
  config,
  overrideSize,
}: {
  config: LogoDownloadConfig;
  overrideSize?: number;
}) {
  const { brand, variant, color } = config;
  const size = overrideSize ?? config.size;

  if (brand === "one-nexty") {
    if (variant === "mark") return <OneNextyMark color={color} size={size} />;
    return <OneNextyLogo variant={variant as any} color={color} size={size} />;
  }

  if (brand === "nexty") {
    if (variant === "mark") return <NextyBrandMark color={color} size={size} />;
    if (variant === "wordmark") return <NextyWordmark color={color} size={size} />;
    if (variant === "app-icon") return <NextyAppIcon size={size} />;
    if (variant === "favicon") return <NextyFavicon size={size} />;
    return <NextyBrandLogo variant={variant as any} color={color} size={size} />;
  }

  if (brand === "design-system") {
    if (variant === "mark") return <NextyDesignSystemMark color={color} size={size} />;
    return <NextyDesignSystemLogo variant={variant as any} color={color} size={size} />;
  }

  if (brand === "arthur") {
    if (variant === "mark") return <NextyArthurMark color={color} size={size} />;
    return <NextyArthurLogo variant={variant as any} color={color} size={size} />;
  }

  if (brand === "cloud") {
    if (variant === "mark") return <NextyCloudMark color={color} size={size} />;
    return <NextyCloudLogo variant={variant as any} color={color} size={size} />;
  }

  return null;
}

function generateReactSnippet(config: LogoDownloadConfig): string {
  if (config.brand === "one-nexty") {
    if (config.variant === "mark") {
      return `import { OneNextyMark } from "@one-nexty/icons";\n\n<OneNextyMark color="${config.color}" size={${config.size}} />`;
    }
    return `import { OneNextyLogo } from "@one-nexty/icons";\n\n<OneNextyLogo variant="${config.variant}" color="${config.color}" size={${config.size}} />`;
  } else if (config.brand === "nexty") {
    if (config.variant === "mark") {
      return `import { NextyBrandMark } from "@one-nexty/icons";\n\n<NextyBrandMark color="${config.color}" size={${config.size}} />`;
    }
    if (config.variant === "wordmark") {
      return `import { NextyWordmark } from "@one-nexty/icons";\n\n<NextyWordmark color="${config.color}" size={${config.size}} />`;
    }
    if (config.variant === "app-icon") {
      return `import { NextyAppIcon } from "@one-nexty/icons";\n\n<NextyAppIcon size={${config.size}} />`;
    }
    if (config.variant === "favicon") {
      return `import { NextyFavicon } from "@one-nexty/icons";\n\n<NextyFavicon size={${config.size}} />`;
    }
    return `import { NextyBrandLogo } from "@one-nexty/icons";\n\n<NextyBrandLogo variant="${config.variant}" color="${config.color}" size={${config.size}} />`;
  } else if (config.brand === "design-system") {
    if (config.variant === "mark") {
      return `import { NextyDesignSystemMark } from "@one-nexty/icons";\n\n<NextyDesignSystemMark color="${config.color}" size={${config.size}} />`;
    }
    return `import { NextyDesignSystemLogo } from "@one-nexty/icons";\n\n<NextyDesignSystemLogo variant="${config.variant}" color="${config.color}" size={${config.size}} />`;
  } else if (config.brand === "arthur") {
    if (config.variant === "mark") {
      return `import { NextyArthurMark } from "@one-nexty/icons";\n\n<NextyArthurMark color="${config.color}" size={${config.size}} />`;
    }
    return `import { NextyArthurLogo } from "@one-nexty/icons";\n\n<NextyArthurLogo variant="${config.variant}" color="${config.color}" size={${config.size}} />`;
  } else if (config.brand === "cloud") {
    if (config.variant === "mark") {
      return `import { NextyCloudMark } from "@one-nexty/icons";\n\n<NextyCloudMark color="${config.color}" size={${config.size}} />`;
    }
    return `import { NextyCloudLogo } from "@one-nexty/icons";\n\n<NextyCloudLogo variant="${config.variant}" color="${config.color}" size={${config.size}} />`;
  }

  return "";
}
